import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { GoogleMapsLoaderService } from '../../../../../services/google-maps/google-maps-loader.service';
import { environment } from '../../../../../../environments/environment';

declare const google: any;

/**
 * Representa los datos mínimos de una sucursal para pintarla en el mapa.
 */
export interface SucursalMapa {
  branchDescription: string;
  latitude: string;
  longitude: string;
}

/**
 * Muestra un mapa de Google con un marcador por cada sucursal recibida,
 * reencuadrando la vista cada vez que cambia la lista.
 */
@Component({
  selector: 'app-mapa-sucursales',
  standalone: true,
  templateUrl: './mapa-sucursales.component.html',
  styleUrl: './mapa-sucursales.component.css',
})
export class MapaSucursalesComponent implements AfterViewInit {
  /** Contenedor del DOM donde se renderiza el mapa */
  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  /** Instancia del mapa de Google */
  private map?: any;
  /** Marcadores actualmente pintados en el mapa */
  private markers: any[] = [];
  /** Sucursales pendientes de pintar o actualmente pintadas */
  private branchList: SucursalMapa[] = [];

  /**
   * Recibe las sucursales a marcar y repinta el mapa cuando cambian.
   */
  @Input({ required: true })
  set branches(value: SucursalMapa[]) {
    this.branchList = value ?? [];
    this.updateMapMarkers();
  }

  constructor(private googleMapsLoader: GoogleMapsLoaderService) {}

  ngAfterViewInit(): void {
    this.initMap();
  }

  /**
   * Carga el script de Google Maps y crea la instancia del mapa
   * en el contenedor, pintando los marcadores iniciales.
   */
  private initMap() {
    this.googleMapsLoader
      .load()
      .then(() => {
        this.map = new google.maps.Map(this.mapContainer.nativeElement, {
          center: { lat: 18.735693, lng: -70.162651 },
          zoom: 8,
          mapId: environment.fe_google_maps_map_id,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        this.updateMapMarkers();
      })
      .catch((error) => {
        console.error('Error al cargar Google Maps:', error);
      });
  }

  /**
   * Limpia los marcadores actuales y pinta uno por cada sucursal
   * recibida, ajustando el encuadre del mapa.
   */
  private updateMapMarkers() {
    if (!this.map) {
      return;
    }

    this.markers.forEach((marker) => (marker.map = null));
    this.markers = [];

    const bounds = new google.maps.LatLngBounds();

    for (const branch of this.branchList) {
      const position = {
        lat: parseFloat(branch.latitude),
        lng: parseFloat(branch.longitude),
      };
      if (isNaN(position.lat) || isNaN(position.lng)) {
        continue;
      }
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position,
        title: branch.branchDescription,
      });
      this.markers.push(marker);
      bounds.extend(position);
    }

    if (this.markers.length === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(15);
    } else if (this.markers.length > 1) {
      this.map.fitBounds(bounds, 40);
    }
  }
}
